// Проектная документация (ПСД): список + богатый сайдбар + создание/загрузка.
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FolderKanban, Plus, FileText, MessageSquare } from 'lucide-react';
import { projectsApi, contractsApi, getErrorMessage } from '@/api';
import { AppLayout, PageHeader } from '@/components/layout/AppLayout';
import { RichSidebar } from '@/components/layout/RichSidebar';
import { Spinner } from '@/components/ui/Spinner';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { EmptyState } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/hooks/useAuth';
import { formatDate } from '@/lib/format';

const STATUS_COLOR: Record<string, string> = {
  draft: 'var(--text-muted)', review: 'var(--yellow)',
  approved: 'var(--green)', rejected: 'var(--red)',
};

export function ProjectsPage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(false);

  const status = params.get('status') || undefined;
  const { data, isLoading } = useQuery({
    queryKey: ['projects', { status }],
    queryFn: () => projectsApi.list(status ? { status } : {}),
  });

  const title = status === 'review' ? 'Проверка проектов' : 'Проектная документация';

  return (
    <AppLayout sidebar={<RichSidebar />}>
      <PageHeader title={title} subtitle={data ? `${data.count} проектов` : undefined}
        actions={can('projects', 'add') && (
          <Button variant="primary" onClick={() => setCreateOpen(true)}><Plus size={16} /> Новый проект</Button>
        )} />
      {isLoading ? <Spinner label="Загрузка…" /> :
        !data || data.results.length === 0 ? (
          <EmptyState icon={<FolderKanban size={32} />} title="Проектов нет"
            hint={status ? 'В этом разделе пока пусто' : 'Загрузите проектную документацию'} />
        ) : (
          <div className="panel">
            <table className="data-table">
              <thead>
                <tr><th>Номер ПСД</th><th>Договор</th><th>Страниц</th><th>Замечаний</th><th>Статус</th><th>Создан</th></tr>
              </thead>
              <tbody>
                {data.results.map((p) => (
                  <tr key={p.id} onClick={() => navigate(`/projects/${p.id}`)} style={{ cursor: 'pointer' }}>
                    <td className="mono" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <FileText size={15} style={{ color: 'var(--accent)' }} />{p.number}
                    </td>
                    <td>{p.contract_number}</td>
                    <td className="mono">{p.pages_count}</td>
                    <td>
                      {p.annotations.length > 0 ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--yellow)' }}>
                          <MessageSquare size={13} />{p.annotations.length}
                        </span>
                      ) : '—'}
                    </td>
                    <td><StatusBadge label={p.status_display} color={STATUS_COLOR[p.status]} /></td>
                    <td className="mono">{formatDate(p.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      <CreateProjectModal open={createOpen} onClose={() => setCreateOpen(false)}
        onCreated={(id) => { qc.invalidateQueries({ queryKey: ['projects'] }); qc.invalidateQueries({ queryKey: ['nav-counts'] }); toast('Проект создан', 'success'); navigate(`/projects/${id}`); }}
        onError={(m) => toast(m, 'error')} />
    </AppLayout>
  );
}

function CreateProjectModal({ open, onClose, onCreated, onError }: {
  open: boolean; onClose: () => void; onCreated: (id: number) => void; onError: (m: string) => void;
}) {
  const [number, setNumber] = useState('');
  const [contract, setContract] = useState<number | ''>('');
  const [pdf, setPdf] = useState<File | null>(null);
  const { data: contracts } = useQuery({ queryKey: ['contracts-all'], queryFn: () => contractsApi.list({}), enabled: open });
  const mutation = useMutation({
    mutationFn: () => projectsApi.create({ number, contract: Number(contract) }, pdf ?? undefined),
    onSuccess: (p) => { onClose(); onCreated(p.id); },
    onError: (e) => onError(getErrorMessage(e)),
  });
  return (
    <Modal open={open} onClose={onClose} title="Новый проект (ПСД)"
      footer={<>
        <Button onClick={onClose}>Отмена</Button>
        <Button variant="primary" disabled={!number || !contract || mutation.isPending} onClick={() => mutation.mutate()}>
          {mutation.isPending ? 'Создание…' : 'Создать'}
        </Button>
      </>}>
      <div className="field">
        <label className="field__label">Номер ПСД</label>
        <input className="field__input" value={number} onChange={(e) => setNumber(e.target.value)} placeholder="ПСД-2025-001" />
      </div>
      <div className="field">
        <label className="field__label">Договор</label>
        <select className="field__select" value={contract} onChange={(e) => setContract(e.target.value ? Number(e.target.value) : '')}>
          <option value="">— выберите —</option>
          {contracts?.results.map((c) => <option key={c.id} value={c.id}>{c.number}</option>)}
        </select>
      </div>
      <div className="field">
        <label className="field__label">PDF проекта (необязательно)</label>
        <input type="file" accept="application/pdf" className="field__input"
          onChange={(e) => setPdf(e.target.files?.[0] ?? null)} />
      </div>
    </Modal>
  );
}
